import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Type } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule
} from '@ngx-formly/core';
import { Button } from '@openng/optimus-ui/button';
import { StepperModule } from '@openng/optimus-ui/stepper';
import { interval, Observable, of, throttle } from 'rxjs';

interface StepProps extends FormlyFieldProps {
  linear?: boolean;
  orientation?: 'horizontal' | 'vertical';
  isTopNavigationVisible?: boolean;
  isBottomNavigationVisible?: boolean;
}

export interface StepFieldConfig extends FormlyFieldConfig<StepProps> {
  type?: 'step' | Type<StepTypeComponent>;
}

@Component({
  selector: 'lib-step-type',
  templateUrl: './step-type.component.html',
  styleUrl: './step-type.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslocoDirective,
    NgClass,
    FormlyModule,
    StepperModule,
    AsyncPipe,
    Button,
    NgTemplateOutlet
  ]
})
export class StepTypeComponent extends FieldType<FieldTypeConfig<StepProps>> {
  errorCount(field: FormlyFieldConfig): number {
    if (field.hide) {
      return 0;
    }
    let count = 0;
    if (field.key) {
      if (
        field.formControl?.invalid &&
        (field.formControl?.touched || field.options?.parentForm?.submitted)
      ) {
        count = 1;
      } else {
        count = 0;
      }
    } else if (field.fieldGroup) {
      count = field.fieldGroup.reduce((total, item) => {
        return total + this.errorCount(item);
      }, 0);
    }
    return count;
  }

  errorCount$(field: FormlyFieldConfig): Observable<number> {
    return of(this.errorCount(field)).pipe(throttle(() => interval(1000)));
  }

  onStepChange() {
    if (this.props.orientation === 'horizontal') {
      setTimeout(() => {
        const activeStep = document.querySelector(`p-step[data-p-active="true"]`);
        if (activeStep) {
          activeStep.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }, 0);
    }
  }

  onNext(step: FormlyFieldConfig, activateCallback: (val: number) => void, nextValue: number) {
    if (this.isStepInvalid(step)) {
      this.markFieldGroupTouched(step);
      return;
    }
    activateCallback(nextValue);
  }

  private markFieldGroupTouched(field: FormlyFieldConfig) {
    if (field.key && field.formControl) {
      field.formControl.markAsTouched();
    }

    if (field.fieldGroup) {
      field.fieldGroup.forEach(f => this.markFieldGroupTouched(f));
    }
  }

  private isStepInvalid(field: FormlyFieldConfig): boolean {
    if (field.key && field.formControl) {
      return field.formControl.invalid;
    }

    if (field.fieldGroup) {
      return field.fieldGroup.some(f => this.isStepInvalid(f));
    }

    return false;
  }
}
